/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `aviation_attitude` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class AviationAttitudeMesg : Mesg {
    public constructor() : super("AviationAttitude", Profile.MesgNum.AVIATION_ATTITUDE)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. Timestamp message was output */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** timestamp_ms, in ms. Fractional part of timestamp, added to timestamp */
    public var timestampMs: UShort?
        get() = getFieldValue(TIMESTAMP_MS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TIMESTAMP_MS_FIELD_NUM, value)

    /** Number of values stored in system_time. */
    public val numSystemTime: Int
        get() = getField(SYSTEM_TIME_FIELD_NUM)?.numValues ?: 0

    /** system_time, in ms. System time associated with sample expressed in ms. */
    public var systemTime: List<UInt?>?
        get() {
            val field = getField(SYSTEM_TIME_FIELD_NUM) ?: return null
            return List(field.numValues) { getSystemTime(it) }
        }
        set(value) {
            removeField(SYSTEM_TIME_FIELD_NUM)
            value?.forEachIndexed { index, element -> setSystemTime(index, element) }
        }

    public fun getSystemTime(index: Int): UInt? =
        getFieldValue(SYSTEM_TIME_FIELD_NUM, index) as? UInt

    public fun setSystemTime(index: Int, value: UInt?) {
        setFieldValue(SYSTEM_TIME_FIELD_NUM, value, index)
    }

    /** Number of values stored in pitch. */
    public val numPitch: Int
        get() = getField(PITCH_FIELD_NUM)?.numValues ?: 0

    /** pitch, in radians. Range -PI/2 to +PI/2 */
    public var pitch: List<Double?>?
        get() {
            val field = getField(PITCH_FIELD_NUM) ?: return null
            return List(field.numValues) { getPitch(it) }
        }
        set(value) {
            removeField(PITCH_FIELD_NUM)
            value?.forEachIndexed { index, element -> setPitch(index, element) }
        }

    public fun getPitch(index: Int): Double? =
        getFieldValue(PITCH_FIELD_NUM, index) as? Double

    public fun setPitch(index: Int, value: Double?) {
        setFieldValue(PITCH_FIELD_NUM, value, index)
    }

    /** Number of values stored in roll. */
    public val numRoll: Int
        get() = getField(ROLL_FIELD_NUM)?.numValues ?: 0

    /** roll, in radians. Range -PI to +PI */
    public var roll: List<Double?>?
        get() {
            val field = getField(ROLL_FIELD_NUM) ?: return null
            return List(field.numValues) { getRoll(it) }
        }
        set(value) {
            removeField(ROLL_FIELD_NUM)
            value?.forEachIndexed { index, element -> setRoll(index, element) }
        }

    public fun getRoll(index: Int): Double? =
        getFieldValue(ROLL_FIELD_NUM, index) as? Double

    public fun setRoll(index: Int, value: Double?) {
        setFieldValue(ROLL_FIELD_NUM, value, index)
    }

    /** Number of values stored in accel_lateral. */
    public val numAccelLateral: Int
        get() = getField(ACCEL_LATERAL_FIELD_NUM)?.numValues ?: 0

    /** accel_lateral, in m/s^2. Range -78.4 to +78.4 (-8 Gs to 8 Gs) */
    public var accelLateral: List<Double?>?
        get() {
            val field = getField(ACCEL_LATERAL_FIELD_NUM) ?: return null
            return List(field.numValues) { getAccelLateral(it) }
        }
        set(value) {
            removeField(ACCEL_LATERAL_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAccelLateral(index, element) }
        }

    public fun getAccelLateral(index: Int): Double? =
        getFieldValue(ACCEL_LATERAL_FIELD_NUM, index) as? Double

    public fun setAccelLateral(index: Int, value: Double?) {
        setFieldValue(ACCEL_LATERAL_FIELD_NUM, value, index)
    }

    /** Number of values stored in accel_normal. */
    public val numAccelNormal: Int
        get() = getField(ACCEL_NORMAL_FIELD_NUM)?.numValues ?: 0

    /** accel_normal, in m/s^2. Range -78.4 to +78.4 (-8 Gs to 8 Gs) */
    public var accelNormal: List<Double?>?
        get() {
            val field = getField(ACCEL_NORMAL_FIELD_NUM) ?: return null
            return List(field.numValues) { getAccelNormal(it) }
        }
        set(value) {
            removeField(ACCEL_NORMAL_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAccelNormal(index, element) }
        }

    public fun getAccelNormal(index: Int): Double? =
        getFieldValue(ACCEL_NORMAL_FIELD_NUM, index) as? Double

    public fun setAccelNormal(index: Int, value: Double?) {
        setFieldValue(ACCEL_NORMAL_FIELD_NUM, value, index)
    }

    /** Number of values stored in turn_rate. */
    public val numTurnRate: Int
        get() = getField(TURN_RATE_FIELD_NUM)?.numValues ?: 0

    /** turn_rate, in radians/second. Range -8.727 to +8.727 (-500 degs/sec to +500 degs/sec) */
    public var turnRate: List<Double?>?
        get() {
            val field = getField(TURN_RATE_FIELD_NUM) ?: return null
            return List(field.numValues) { getTurnRate(it) }
        }
        set(value) {
            removeField(TURN_RATE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTurnRate(index, element) }
        }

    public fun getTurnRate(index: Int): Double? =
        getFieldValue(TURN_RATE_FIELD_NUM, index) as? Double

    public fun setTurnRate(index: Int, value: Double?) {
        setFieldValue(TURN_RATE_FIELD_NUM, value, index)
    }

    /** Number of values stored in stage. */
    public val numStage: Int
        get() = getField(STAGE_FIELD_NUM)?.numValues ?: 0

    /** stage. */
    public var stage: List<AttitudeStage?>?
        get() {
            val field = getField(STAGE_FIELD_NUM) ?: return null
            return List(field.numValues) { getStage(it) }
        }
        set(value) {
            removeField(STAGE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setStage(index, element) }
        }

    public fun getStage(index: Int): AttitudeStage? =
        (getFieldValue(STAGE_FIELD_NUM, index) as? UByte)?.let { AttitudeStage.fromValue(it) }

    public fun setStage(index: Int, value: AttitudeStage?) {
        setFieldValue(STAGE_FIELD_NUM, value?.value, index)
    }

    /** Number of values stored in attitude_stage_complete. */
    public val numAttitudeStageComplete: Int
        get() = getField(ATTITUDE_STAGE_COMPLETE_FIELD_NUM)?.numValues ?: 0

    /** attitude_stage_complete, in %. The percent complete of the current attitude stage. Set to 0 for attitude stages 0, 1 and 2 and to 100 for attitude stage 3 by AHRS modules that do not support it. Range - 100 */
    public var attitudeStageComplete: List<UByte?>?
        get() {
            val field = getField(ATTITUDE_STAGE_COMPLETE_FIELD_NUM) ?: return null
            return List(field.numValues) { getAttitudeStageComplete(it) }
        }
        set(value) {
            removeField(ATTITUDE_STAGE_COMPLETE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAttitudeStageComplete(index, element) }
        }

    public fun getAttitudeStageComplete(index: Int): UByte? =
        getFieldValue(ATTITUDE_STAGE_COMPLETE_FIELD_NUM, index) as? UByte

    public fun setAttitudeStageComplete(index: Int, value: UByte?) {
        setFieldValue(ATTITUDE_STAGE_COMPLETE_FIELD_NUM, value, index)
    }

    /** Number of values stored in track. */
    public val numTrack: Int
        get() = getField(TRACK_FIELD_NUM)?.numValues ?: 0

    /** track, in radians. Track Angle/Heading Range 0 - 2pi */
    public var track: List<Double?>?
        get() {
            val field = getField(TRACK_FIELD_NUM) ?: return null
            return List(field.numValues) { getTrack(it) }
        }
        set(value) {
            removeField(TRACK_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTrack(index, element) }
        }

    public fun getTrack(index: Int): Double? =
        getFieldValue(TRACK_FIELD_NUM, index) as? Double

    public fun setTrack(index: Int, value: Double?) {
        setFieldValue(TRACK_FIELD_NUM, value, index)
    }

    /** Number of values stored in validity. */
    public val numValidity: Int
        get() = getField(VALIDITY_FIELD_NUM)?.numValues ?: 0

    /** validity. */
    public var validity: List<UShort?>?
        get() {
            val field = getField(VALIDITY_FIELD_NUM) ?: return null
            return List(field.numValues) { getValidity(it) }
        }
        set(value) {
            removeField(VALIDITY_FIELD_NUM)
            value?.forEachIndexed { index, element -> setValidity(index, element) }
        }

    public fun getValidity(index: Int): UShort? =
        getFieldValue(VALIDITY_FIELD_NUM, index) as? UShort

    public fun setValidity(index: Int, value: UShort?) {
        setFieldValue(VALIDITY_FIELD_NUM, value, index)
    }

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val TIMESTAMP_MS_FIELD_NUM: UByte = 0u
        public const val SYSTEM_TIME_FIELD_NUM: UByte = 1u
        public const val PITCH_FIELD_NUM: UByte = 2u
        public const val ROLL_FIELD_NUM: UByte = 3u
        public const val ACCEL_LATERAL_FIELD_NUM: UByte = 4u
        public const val ACCEL_NORMAL_FIELD_NUM: UByte = 5u
        public const val TURN_RATE_FIELD_NUM: UByte = 6u
        public const val STAGE_FIELD_NUM: UByte = 7u
        public const val ATTITUDE_STAGE_COMPLETE_FIELD_NUM: UByte = 8u
        public const val TRACK_FIELD_NUM: UByte = 9u
        public const val VALIDITY_FIELD_NUM: UByte = 10u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("TimestampMs", 0u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[1] = Field("SystemTime", 1u, BaseType.UINT32, 1.0, 0.0, "ms", false)
            fields[2] = Field("Pitch", 2u, BaseType.SINT16, 10430.38, 0.0, "radians", false)
            fields[3] = Field("Roll", 3u, BaseType.SINT16, 10430.38, 0.0, "radians", false)
            fields[4] = Field("AccelLateral", 4u, BaseType.SINT16, 100.0, 0.0, "m/s^2", false)
            fields[5] = Field("AccelNormal", 5u, BaseType.SINT16, 100.0, 0.0, "m/s^2", false)
            fields[6] = Field("TurnRate", 6u, BaseType.SINT16, 1024.0, 0.0, "radians/second", false)
            fields[7] = Field("Stage", 7u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[8] = Field("AttitudeStageComplete", 8u, BaseType.UINT8, 1.0, 0.0, "%", false)
            fields[9] = Field("Track", 9u, BaseType.UINT16, 10430.38, 0.0, "radians", false)
            fields[10] = Field("Validity", 10u, BaseType.UINT16, 1.0, 0.0, "", false)
            return fields
        }
    }
}
